import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { toast } from 'sonner';
import { Download, FileText, Filter, Columns3, ChevronDown, ChevronUp, ArrowUpDown, GripVertical } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOSS_TYPES = [
  { key: 'al', label: 'Auto Liability' },
  { key: 'pd', label: 'Physical Damage' },
  { key: 'cd', label: 'Cargo Damage' },
  { key: 'ocac', label: 'Occupational Accident' },
  { key: 'wc', label: 'Workers Compensation' },
];

const ALL_COLUMNS = [
  { key: 'report_no', label: 'Report No' },
  { key: 'date_of_loss', label: 'Date of Loss' },
  { key: 'drivers_name', label: 'Driver' },
  { key: 'truck_no', label: 'Truck' },
  { key: 'trailer_no', label: 'Trailer' },
  { key: 'location', label: 'Location' },
  { key: 'initial_amount', label: 'Initial Amount' },
  { key: 'final_amount', label: 'Final Amount' },
  { key: 'submittal', label: 'Submittal' },
  { key: 'status', label: 'Status' },
  { key: 'fault', label: 'Fault' },
  { key: 'description', label: 'Description' },
  { key: 'loss_types', label: 'Loss Types' },
  { key: 'driver_charged', label: 'Driver Charged' },
];

const DEFAULT_COLUMNS = ['report_no', 'date_of_loss', 'drivers_name', 'truck_no', 'trailer_no', 'location', 'initial_amount'];

const REPORTING_OPTIONS = [
  { key: 'all', label: 'ALL' },
  { key: 'insurance', label: 'Reported to Insurance' },
  { key: 'inhouse', label: 'In-House' },
  { key: 'pending', label: 'Pending' },
  { key: 'na', label: 'N/A' },
];

const REPORT_TYPE_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'loss', label: 'Loss' },
  { key: 'recovery', label: 'Recovery' },
  { key: 'unknown', label: 'Unknown' },
];

const DATE_PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'year', label: 'This Year' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

export default function LossReports() {
  const { user, activeOrgId, userOrgs } = useAuth();
  const isProvider = user?.account_type === 'provider';
  const isInOrgView = isProvider && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedType, setSelectedType] = useState('al');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportingStatus, setReportingStatus] = useState('all');
  const [reportType, setReportType] = useState('all');
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLUMNS);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('date_of_loss');
  const [sortDir, setSortDir] = useState('desc');
  const [policyPeriods, setPolicyPeriods] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [exporting, setExporting] = useState(false);

  // Fetch companies
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/companies`, { withCredentials: true });
        const names = (res.data || []).map(c => c.name).sort();
        setCompanies(names);
        if (names.length > 0) setSelectedCompany(names[0]);
      } catch { /* ignore */ }
    };
    fetch();
  }, [activeOrgId]);

  // Fetch policy periods when company or type changes
  useEffect(() => {
    if (!selectedCompany) { setPolicyPeriods([]); return; }
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/company-insurance-policies`, { params: { company_name: selectedCompany }, withCredentials: true });
        const entries = res.data?.[selectedType] || [];
        setPolicyPeriods(entries);
        setSelectedPolicy('');
      } catch { setPolicyPeriods([]); }
    };
    fetch();
  }, [selectedCompany, selectedType]);

  // Compute date range from preset
  useEffect(() => {
    if (selectedPolicy) return; // policy period overrides
    const now = new Date();
    if (datePreset === 'all') { setDateFrom(''); setDateTo(''); }
    else if (datePreset === 'year') { setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(`${now.getFullYear()}-12-31`); }
    else if (datePreset === 'quarter') {
      const q = Math.floor(now.getMonth() / 3);
      const qs = new Date(now.getFullYear(), q * 3, 1);
      const qe = new Date(now.getFullYear(), q * 3 + 3, 0);
      setDateFrom(qs.toISOString().slice(0, 10));
      setDateTo(qe.toISOString().slice(0, 10));
    } else if (datePreset === 'month') {
      const ms = new Date(now.getFullYear(), now.getMonth(), 1);
      const me = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDateFrom(ms.toISOString().slice(0, 10));
      setDateTo(me.toISOString().slice(0, 10));
    }
  }, [datePreset, selectedPolicy]);

  // When policy period selected, override date range
  useEffect(() => {
    if (!selectedPolicy) return;
    const p = policyPeriods.find(pp => pp.id === selectedPolicy);
    if (p) { setDateFrom(p.start_date); setDateTo(p.end_date); setDatePreset('custom'); }
  }, [selectedPolicy, policyPeriods]);

  // Fetch claims
  useEffect(() => {
    if (!selectedCompany) return;
    const fetchClaims = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/claims`, { params: { company_name: selectedCompany }, withCredentials: true });
        setClaims(res.data || []);
      } catch { setClaims([]); }
      finally { setLoading(false); }
    };
    fetchClaims();
  }, [selectedCompany]);

  // Helper: resolve submittal from type_data or legacy flat fields
  const getSubmittal = useCallback((claim) => {
    const td = claim.type_data?.[selectedType] || {};
    return td.submittal || claim[`${selectedType}_submittal`] || claim.submittal || '';
  }, [selectedType]);

  // Filter claims by loss type, date range, and reporting status
  const filteredClaims = useMemo(() => {
    let result = claims.filter(c => c[selectedType]);
    // Date filter
    if (dateFrom || dateTo) {
      result = result.filter(c => {
        const dol = (c.date_of_loss || '').slice(0, 10);
        if (!dol) return false;
        if (dateFrom && dol < dateFrom) return false;
        if (dateTo && dol > dateTo) return false;
        return true;
      });
    }
    // Report type filter (Loss = At Fault, Recovery = Not At Fault)
    if (reportType === 'loss') {
      result = result.filter(c => c.fault === 'At Fault');
    } else if (reportType === 'recovery') {
      result = result.filter(c => c.fault === 'Not At Fault');
    } else if (reportType === 'unknown') {
      result = result.filter(c => !c.fault || c.fault === 'Unknown');
    }
    // Reporting status filter
    if (reportingStatus === 'insurance') {
      result = result.filter(c => getSubmittal(c) === 'Insurance');
    } else if (reportingStatus === 'inhouse') {
      result = result.filter(c => getSubmittal(c) === 'In-House');
    } else if (reportingStatus === 'pending') {
      result = result.filter(c => {
        const sub = getSubmittal(c);
        return !sub || sub === 'Pending';
      });
    } else if (reportingStatus === 'na') {
      result = result.filter(c => getSubmittal(c) === 'NA');
    }
    // Sort
    result.sort((a, b) => {
      let va = a[sortKey] || '';
      let vb = b[sortKey] || '';
      if (sortKey === 'amount') { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; }
      else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [claims, selectedType, reportType, reportingStatus, sortKey, sortDir, dateFrom, dateTo, getSubmittal]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      // Add in the order defined in ALL_COLUMNS
      const allKeys = ALL_COLUMNS.map(c => c.key);
      const next = [...prev, key];
      next.sort((a, b) => allKeys.indexOf(a) - allKeys.indexOf(b));
      return next;
    });
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setVisibleCols(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  // Ordered visible columns (use visibleCols order, not ALL_COLUMNS order)
  const orderedCols = visibleCols.map(k => ALL_COLUMNS.find(c => c.key === k)).filter(Boolean);

  const getCellValue = (claim, key) => {
    if (key === 'initial_amount' || key === 'final_amount') {
      const td = claim.type_data?.[selectedType] || {};
      const val = td[key] || claim[`${selectedType}_${key}`] || claim[key] || '';
      return val ? `$${Number(val).toLocaleString()}` : '';
    }
    if (key === 'submittal') {
      return getSubmittal(claim) || 'Pending';
    }
    if (key === 'date_of_loss') {
      const d = claim.date_of_loss;
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
    }
    if (key === 'loss_types') {
      return LOSS_TYPES.filter(t => claim[t.key]).map(t => t.label).join(', ');
    }
    if (key === 'driver_charged') {
      if (!claim.driver_charged || claim.driver_charged === '') return '-';
      if (claim.driver_charged !== 'Yes') return 'No';
      if (claim.deductible && claim.deductible_amount) return `Deductible: $${Number(claim.deductible_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      if (claim.other_amount_checked && claim.other_amount) return `Other: $${Number(claim.other_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return 'Yes';
    }
    return claim[key] || '';
  };

  const getReportTitle = () => {
    const lt = LOSS_TYPES.find(t => t.key === selectedType)?.label || '';
    return `${selectedCompany} - ${lt}`;
  };

  const getDateLabel = () => {
    if (dateFrom && dateTo) return `${dateFrom} to ${dateTo}`;
    if (dateFrom) return `From ${dateFrom}`;
    if (dateTo) return `To ${dateTo}`;
    return 'All Time';
  };

  const getAmount = (claim, key) => Number(claim.type_data?.[selectedType]?.[key] || claim[`${selectedType}_${key}`] || claim[key]) || 0;

  const getDriverChargedAmount = (claim) => {
    if (claim.driver_charged !== 'Yes') return 0;
    if (claim.deductible && claim.deductible_amount) return Number(claim.deductible_amount) || 0;
    if (claim.other_amount_checked && claim.other_amount) return Number(claim.other_amount) || 0;
    return 0;
  };

  const exportCSV = () => {
    setExporting(true);
    try {
      const cols = orderedCols;
      const header = cols.map(c => c.label);
      const rows = filteredClaims.map(claim => cols.map(c => getCellValue(claim, c.key)));
      const initialAmtTotal = filteredClaims.reduce((sum, c) => sum + getAmount(c, 'initial_amount'), 0);
      const finalAmtTotal = filteredClaims.reduce((sum, c) => sum + getAmount(c, 'final_amount'), 0);
      const driverChargedTotal = filteredClaims.reduce((sum, c) => sum + getDriverChargedAmount(c), 0);
      const footerRow = cols.map(c => {
        if (c.key === 'report_no') return `Count: ${filteredClaims.length}`;
        if (c.key === 'initial_amount') return `$${initialAmtTotal.toLocaleString()}`;
        if (c.key === 'final_amount') return `$${finalAmtTotal.toLocaleString()}`;
        if (c.key === 'driver_charged') return `$${driverChargedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return '';
      });
      const lines = [
        `CMS Report`,
        `${getReportTitle()}`,
        `Date Range: ${getDateLabel()}`,
        `Generated: ${new Date().toLocaleDateString()}`,
        '',
        header.join(','),
        ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
        footerRow.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loss_report_${selectedCompany.replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('CSV export failed'); }
    finally { setExporting(false); }
  };

  const exportPDF = () => {
    setExporting(true);
    try {
      const cols = orderedCols;
      const doc = new jsPDF({ orientation: 'landscape' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Logo + Header
      try {
        const logoImg = new Image();
        logoImg.src = '/logo.png';
        doc.addImage(logoImg, 'PNG', 14, 8, 12, 12);
      } catch { /* logo optional */ }
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175);
      doc.text('CMS Report', 28, 17);

      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(getReportTitle(), 14, 28);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const policyLabel = selectedPolicy ? policyPeriods.find(p => p.id === selectedPolicy) : null;
      let subLine = `Date Range: ${getDateLabel()}`;
      if (policyLabel) subLine += `  |  Policy: ${policyLabel.policy_no}`;
      subLine += `  |  Generated: ${new Date().toLocaleDateString()}`;
      doc.text(subLine, 14, 35);

      // Separator line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(14, 37, pageW - 14, 37);

      // Table body
      const head = [cols.map(c => c.label)];
      const body = filteredClaims.map(claim => cols.map(c => getCellValue(claim, c.key)));

      // Footer row
      const initialAmtTotal = filteredClaims.reduce((sum, c) => sum + getAmount(c, 'initial_amount'), 0);
      const finalAmtTotal = filteredClaims.reduce((sum, c) => sum + getAmount(c, 'final_amount'), 0);
      const driverChargedTotal = filteredClaims.reduce((sum, c) => sum + getDriverChargedAmount(c), 0);
      const footerRow = cols.map(c => {
        if (c.key === 'report_no') return `Count: ${filteredClaims.length}`;
        if (c.key === 'initial_amount') return `$${initialAmtTotal.toLocaleString()}`;
        if (c.key === 'final_amount') return `$${finalAmtTotal.toLocaleString()}`;
        if (c.key === 'driver_charged') return `$${driverChargedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return '';
      });
      body.push(footerRow);

      const colCount = cols.length;
      const tableW = pageW - 28;
      const colWidth = tableW / colCount;

      autoTable(doc, {
        startY: 40,
        head,
        body,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 8, cellPadding: 2 },
        bodyStyles: { fontSize: 7.5, cellPadding: 2 },
        columnStyles: Object.fromEntries(cols.map((_, i) => [i, { cellWidth: colWidth }])),
        margin: { left: 14, right: 14, bottom: 22 },
        tableWidth: 'auto',
        styles: { overflow: 'linebreak' },
        willDrawCell: (data) => {
          if (data.section === 'body' && data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 8;
          }
        },
        didDrawPage: (data) => {
          const pg = doc.getCurrentPageInfo().pageNumber;
          const total = doc.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(140, 140, 140);
          doc.text(`CMS Report  |  ${selectedCompany}  |  ${new Date().toLocaleDateString()}`, 14, pageH - 8);
          doc.text(`Page ${pg} of ${total}`, pageW - 14, pageH - 8, { align: 'right' });
        },
      });

      doc.save(`loss_report_${selectedCompany.replace(/\s+/g, '_')}.pdf`);
      toast.success('PDF exported');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="loss-reports-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="loss-reports-title">{isInOrgView ? `${activeOrg?.name || 'Organization'} — Loss Reports` : 'Loss Reports'}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={exporting || filteredClaims.length === 0} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting || filteredClaims.length === 0} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-3" data-testid="report-filters">
        <div className="flex flex-wrap items-end gap-3">
          {/* Company */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-500">Company</Label>
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="h-9 text-sm border border-gray-200 rounded-md px-2 bg-white min-w-[200px]" data-testid="report-company-select">
              {companies.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {/* Loss Type */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-500">Loss Type</Label>
            <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="h-9 text-sm border border-gray-200 rounded-md px-2 bg-white min-w-[160px]" data-testid="report-type-select">
              {LOSS_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {/* Date Presets */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-500">Date Range</Label>
            <div className="flex items-center gap-1">
              {DATE_PRESETS.map(p => (
                <button key={p.key} type="button" onClick={() => { setDatePreset(p.key); setSelectedPolicy(''); }}
                  className={`px-2.5 py-1.5 text-xs rounded-md border transition-colors ${datePreset === p.key && !selectedPolicy ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`report-date-${p.key}`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          {/* Custom Dates */}
          {(datePreset === 'custom' || selectedPolicy) && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-gray-500">From</Label>
                <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); setSelectedPolicy(''); }} className="h-9 text-sm w-36" data-testid="report-date-from" />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-gray-500">To</Label>
                <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); setSelectedPolicy(''); }} className="h-9 text-sm w-36" data-testid="report-date-to" />
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {/* Policy Period */}
          {policyPeriods.length > 0 && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-gray-500">Policy Period</Label>
              <select value={selectedPolicy} onChange={e => setSelectedPolicy(e.target.value)} className="h-9 text-sm border border-gray-200 rounded-md px-2 bg-white min-w-[240px]" data-testid="report-policy-select">
                <option value="">— Select Policy Period —</option>
                {policyPeriods.map(p => (
                  <option key={p.id} value={p.id}>{p.carrier_name ? `${p.carrier_name} — ` : ''}{p.policy_no} ({p.start_date} to {p.end_date})</option>
                ))}
              </select>
            </div>
          )}
          {/* Report Type (Loss / Recovery) */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-500">Report Type</Label>
            <div className="flex items-center gap-1">
              {REPORT_TYPE_OPTIONS.map(o => (
                <button key={o.key} type="button" onClick={() => setReportType(o.key)}
                  className={`px-2.5 py-1.5 text-xs rounded-md border transition-colors ${reportType === o.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`report-type-${o.key}`}
                >{o.label}{o.key === 'loss' ? ' (At Fault)' : o.key === 'recovery' ? ' (Not At Fault)' : ''}</button>
              ))}
            </div>
          </div>
          {/* Reporting Status */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-500">Reporting Status</Label>
            <div className="flex items-center gap-1">
              {REPORTING_OPTIONS.map(o => (
                <button key={o.key} type="button" onClick={() => setReportingStatus(o.key)}
                  className={`px-2.5 py-1.5 text-xs rounded-md border transition-colors ${reportingStatus === o.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`report-status-${o.key}`}
                >{o.label}</button>
              ))}
            </div>
          </div>
          {/* Column Toggle */}
          <div className="flex flex-col gap-1 relative">
            <Label className="text-xs text-gray-500">Columns</Label>
            <button type="button" onClick={() => setColMenuOpen(!colMenuOpen)} className="flex items-center gap-1.5 h-9 px-3 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50" data-testid="report-columns-btn">
              <Columns3 className="w-4 h-4" /> {visibleCols.length}/{ALL_COLUMNS.length} <ChevronDown className="w-3 h-3" />
            </button>
            {colMenuOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[200px]" onMouseLeave={() => setColMenuOpen(false)}>
                {visibleCols.map((key, idx) => {
                  const c = ALL_COLUMNS.find(col => col.key === key);
                  if (!c) return null;
                  return (
                    <div key={c.key} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-50 rounded cursor-grab ${dragIdx === idx ? 'bg-blue-50' : ''}`}>
                      <GripVertical className="w-3 h-3 text-gray-300 shrink-0" />
                      <input type="checkbox" checked onChange={() => toggleCol(c.key)} className="rounded" />
                      {c.label}
                    </div>
                  );
                })}
                <div className="border-t border-gray-100 mt-1 pt-1">
                  {ALL_COLUMNS.filter(c => !visibleCols.includes(c.key)).map(c => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-50 rounded cursor-pointer pl-7">
                      <input type="checkbox" checked={false} onChange={() => toggleCol(c.key)} className="rounded" />
                      <span className="text-gray-400">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500" data-testid="report-count">
          {loading ? 'Loading...' : `${filteredClaims.length} claim${filteredClaims.length !== 1 ? 's' : ''} found`}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto" data-testid="report-table-wrapper">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {orderedCols.map(col => (
                <th key={col.key} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer select-none hover:bg-gray-100" onClick={() => toggleSort(col.key)} data-testid={`report-th-${col.key}`}>
                  <span className="flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredClaims.length === 0 && !loading ? (
              <tr><td colSpan={orderedCols.length} className="text-center py-12 text-gray-400">No claims match the selected filters</td></tr>
            ) : filteredClaims.map((claim, i) => (
              <tr key={claim.report_no || i} className="border-b border-gray-100 hover:bg-blue-50/40 transition-colors" data-testid={`report-row-${i}`}>
                {orderedCols.map(col => (
                  <td key={col.key} className="px-3 py-2 text-gray-700 whitespace-nowrap" data-testid={`report-cell-${col.key}-${i}`}>
                    {getCellValue(claim, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {filteredClaims.length > 0 && (visibleCols.includes('report_no') || visibleCols.includes('initial_amount') || visibleCols.includes('final_amount') || visibleCols.includes('driver_charged')) && (
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold text-sm">
                {orderedCols.map(col => (
                  <td key={col.key} className="px-3 py-2.5 text-gray-800" data-testid={`report-footer-${col.key}`}>
                    {col.key === 'report_no' ? `Count: ${filteredClaims.length}` :
                     col.key === 'initial_amount' ? `$${filteredClaims.reduce((sum, c) => sum + getAmount(c, 'initial_amount'), 0).toLocaleString()}` :
                     col.key === 'final_amount' ? `$${filteredClaims.reduce((sum, c) => sum + getAmount(c, 'final_amount'), 0).toLocaleString()}` :
                     col.key === 'driver_charged' ? `$${filteredClaims.reduce((sum, c) => sum + getDriverChargedAmount(c), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
                     ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
